import type React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import bioplanLogo from '@/svg/bioplanLogo.svg';

import Sidebar from '../Sidebar/Sidebar';
import SidebarBackButton from '../Sidebar/SidebarBackButton';
import SidebarNavDivider from '../Sidebar/SidebarNavDivider';
import SidebarNavItem from '../Sidebar/SidebarNavItem';
import SidebarNavigation from '../Sidebar/SidebarNavigation';

import { useSidebarFilter } from './useSidebarFilter';
import type { SidebarNavItem as SidebarNavItemData } from './useSidebarNavigation';
import { useSidebarNavigation } from './useSidebarNavigation';

function buildIcon(icon: IconComponent | undefined): React.ReactNode {
    if (!icon) return undefined;

    return <SvgIcon icon={icon} size="custom" customSize={20} color="currentColor" />;
}

export interface AppSidebarProps {
    mobileFooter?: ReactNode;
}

export default function AppSidebar(props: AppSidebarProps) {
    const { mobileFooter } = props;
    const { t } = useTranslation();
    const { items, staticItems, backOption, level, params } = useSidebarNavigation();
    const filterItems = useSidebarFilter(level, params);

    const visibleItems = filterItems(items);
    const visibleStaticItems = filterItems(staticItems);

    const renderNavItem = (item: SidebarNavItemData) => {
        const hasChildren = (item.children?.length ?? 0) > 0;

        return (
            <SidebarNavItem
                key={item.id}
                label={t(item.label)}
                highlightLabel={item.highlightLabelKey ? t(item.highlightLabelKey) : undefined}
                href={item.fullUrl}
                isActive={item.isActive}
                isExpanded={hasChildren ? item.isActive : undefined}
                icon={buildIcon(item.icon)}
                subItems={item.children?.map((child) => ({
                    label: t(child.label),
                    href: child.fullUrl,
                    isActive: child.isActive,
                    icon: buildIcon(child.icon),
                    showChevron: child.navigatesToSubLevel,
                    chevronDirection: 'right' as const,
                }))}
            />
        );
    };

    const logo = <img src="/logo.png" alt="OptoScale" style={{ height: 32 }} />;
    const backOptionTitle = backOption ? t(backOption.title) : '';

    return (
        <Sidebar logo={logo} footer={mobileFooter}>
            <SidebarNavigation>
                {backOption && <SidebarBackButton title={backOptionTitle} href={backOption.url} />}

                {visibleItems.map(renderNavItem)}

                {visibleStaticItems.length > 0 && <SidebarNavDivider />}

                {visibleStaticItems.map(renderNavItem)}

                <SidebarNavDivider />

                <BioplanLink />
            </SidebarNavigation>
        </Sidebar>
    );
}

function BioplanLink() {
    return (
        <a
            href="https://bioplan.ai/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 'var(--space-16)' }}>
            <img src={bioplanLogo} alt="Bioplan" style={{ height: 24 }} />
        </a>
    );
}
